/**
 * Iter 280 — Bottom-left deployment-version pill.
 *
 * Why this exists:
 * After 15 attempts to fix the Workbench PDF, the user discovered the
 * problem was deployment lag (production still on Iter 277 code while
 * preview had Iter 279). We need ground truth visible on every page so
 * "did this actually deploy?" stops being a question.
 *
 * What it shows:
 *   ┌──────────────────┐
 *   │ FE v38 · BE iter279 │   ← green = matches, red = mismatch, yellow = backend unreachable
 *   └──────────────────┘
 *
 * - FE version is the service-worker version baked into the bundle
 *   at build time (read from /api/health → frontend's SW_VERSION
 *   compared against the running registration).
 * - BE version is read from /api/health response (`app_version`).
 *
 * The pill is small enough to not interfere with UI, but visible on
 * every screenshot the user sends, so we always know exactly what code
 * was running at the moment they hit a bug.
 *
 * Clicking the pill opens a tiny detail panel with full version info
 * and a "Hard reload" button that unregisters the service worker and
 * forces a fresh download.
 */
import { useEffect, useState } from "react";
import api from "../lib/api";

// Frontend bundle version — keep in sync with service-worker.js SW_VERSION.
// Bumped every iteration that ships frontend code.
export const FE_VERSION = "v115-section-builder";

export default function DeploymentVersionPill() {
  const [be, setBe] = useState({ version: "…", build: "", reachable: null });
  const [open, setOpen] = useState(false);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/health", { timeout: 8000 });
        if (mounted) {
          setBe({
            version: data.app_version || "?",
            build: data.build_time || "",
            reachable: true,
          });
        }
      } catch {
        if (mounted) setBe({ version: "offline", build: "", reachable: false });
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Colour: green = backend reachable + version known; yellow = backend
  // unreachable; (we deliberately don't compare strings — the FE/BE
  // versioning schemes differ. Mismatch detection happens on the user
  // side by reading the pill on a screenshot.)
  const colour = be.reachable === false
    ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
    : "bg-emerald-500/15 border-emerald-500/40 text-emerald-300";

  const hardReload = async () => {
    setReloading(true);
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch { /* ignore */ }
    // Force a no-cache reload.
    window.location.reload();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`fixed left-2 bottom-2 z-[9999] px-2 py-0.5 rounded border text-[10px] font-mono leading-tight ${colour} hover:opacity-100 opacity-70 transition`}
        title="Click for deployment details"
        data-testid="deployment-version-pill"
      >
        FE&nbsp;{FE_VERSION}&nbsp;·&nbsp;BE&nbsp;{be.version}
      </button>
      {open && (
        <div
          className="fixed left-2 bottom-9 z-[9999] w-72 rounded-md border border-white/10 bg-[#0b1320] text-[#e5e7eb] shadow-xl p-3 text-[11px] space-y-2"
          data-testid="deployment-version-detail"
        >
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[12px]">Deployment status</div>
            <button onClick={() => setOpen(false)} className="text-[#647087] hover:text-white text-[10px]">close</button>
          </div>
          <div className="grid grid-cols-2 gap-x-2 gap-y-1 font-mono">
            <div className="text-[#647087]">Frontend bundle</div>
            <div>{FE_VERSION}</div>
            <div className="text-[#647087]">Backend code</div>
            <div className={be.reachable === false ? "text-amber-300" : ""}>{be.version}</div>
            <div className="text-[#647087]">Backend built</div>
            <div className="truncate" title={be.build}>{be.build ? be.build.replace("T", " ").slice(0, 19) : "—"}</div>
            <div className="text-[#647087]">Host</div>
            <div className="truncate" title={typeof window !== "undefined" ? window.location.host : ""}>
              {typeof window !== "undefined" ? window.location.host : "—"}
            </div>
          </div>
          <div className="border-t border-white/5 pt-2 text-[#647087] leading-relaxed">
            If you've just deployed and the version below doesn't reflect it,
            click below to clear the service-worker cache and reload.
          </div>
          <button
            type="button"
            onClick={hardReload}
            disabled={reloading}
            className="w-full hv-btn hv-btn-ghost text-[10px] disabled:opacity-40"
            data-testid="deployment-hard-reload"
          >
            {reloading ? "Reloading…" : "Hard reload (clear SW cache)"}
          </button>
        </div>
      )}
    </>
  );
}
